/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./IdentityHeader.css";

/**
 * The band at the top of a workspace's own dashboard: who this account is,
 * and the two figures it scans for.
 *
 * Shared by the donor and nonprofit dashboards because the design is one
 * component with the logo swapped for initials, not two that happen to look
 * alike -- a donor has no logo and no tagline, so those simply go unset and
 * the same markup renders the shorter card.
 *
 * Everything here is passed in already formatted. The component picks no
 * figures and reads no state: a dashboard's header has to agree with the
 * tiles directly beneath it, which only holds while both come out of the
 * same view model.
 */
class IdentityHeader extends Component {
  render() {
    const p = this.props.profile;
    if (!p) {
      return null;
    }
    const chips = p.chips || [];
    const stats = p.stats || [];
    return (
      <div className="Marketplace-card Marketplace-Identity">
        {/* The tile is the way in to changing the logo -- there is no separate
            button, because the image is the thing being changed and tapping it
            is what people try first. The logo belongs to the organisation
            record, which CheckPlus owns, so this is a third door onto one
            screen rather than a third way to change the image. A donor has no
            organisation to be sent to, so their tile is a plain div. */}
        {p.logoHref ? (
          <a
            className="Marketplace-Identity-mono Marketplace-Identity-mono--link"
            style={{ background: p.monoBg, color: p.monoFg }}
            href={p.logoHref}
            target="_blank"
            rel="noopener noreferrer"
            title={p.logoLabel}
            aria-label={p.logoLabel}
          >
            {p.logo ? (
              <img className="Marketplace-Identity-logo" src={p.logo} alt="" />
            ) : (
              p.mono
            )}
          </a>
        ) : (
          <div
            className="Marketplace-Identity-mono"
            style={{ background: p.monoBg, color: p.monoFg }}
          >
            {p.logo ? (
              <img className="Marketplace-Identity-logo" src={p.logo} alt="" />
            ) : (
              p.mono
            )}
          </div>
        )}
        <div className="Marketplace-Identity-info">
          <div className="Marketplace-Identity-name">{p.name}</div>
          {!!p.tagline && (
            <div className="Marketplace-Identity-tagline">{p.tagline}</div>
          )}
          {!!chips.length && (
            <div className="Marketplace-Identity-chips">
              {chips.map((chip) => {
                /* A donor's chips are plain strings and a nonprofit's carry a
                   tone, so both shapes are accepted here rather than making
                   the donor view model restate what it already says. */
                const c = typeof chip === "string" ? { label: chip } : chip;
                return (
                  <span
                    className={
                      "Marketplace-Identity-chip" +
                      (c.tone ? " Marketplace-Identity-chip--" + c.tone : "")
                    }
                    key={c.label}
                  >
                    {c.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        {!!stats.length && (
          <div className="Marketplace-Identity-stats">
            {stats.map((s) => (
              <div className="Marketplace-Identity-stat" key={s.id}>
                <div className="Marketplace-Identity-statValue">{s.value}</div>
                <div className="Marketplace-Identity-statLabel">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

IdentityHeader.propTypes = {
  /* null before the account behind the dashboard is known -- the sample data
     has none, and a header naming nobody is worse than no header. */
  profile: PropTypes.object,
};

export default IdentityHeader;
